import { Pipe, PipeTransform } from '@angular/core';
import { area, polygon } from '@turf/turf';
import { LatLng } from 'leaflet';
import { IMeasureSegment } from './map';

function calculateDistance(segment: Omit<IMeasureSegment, 'label'>) {
  return segment.from.distanceTo(segment.to);
}

@Pipe({ name: 'distance', standalone: true })
export class DistancePipe implements PipeTransform {
  transform(distance: number): string {
    const displayDistance = distance >= 1000 ? distance / 1000 : distance;
    const unit = distance >= 1000 ? 'km' : 'm';

    const numDigits = distance >= 1000 && distance < 10000 ? 2 : 1;

    return `${displayDistance.toFixed(numDigits)} ${[unit]}`;
  }
}

@Pipe({
  name: 'segmentDistance',
  standalone: true,
})
export class SegmentDistancePipe implements PipeTransform {
  transform(segment: IMeasureSegment): number {
    return calculateDistance(segment);
  }
}

@Pipe({
  name: 'totalDistance',
  standalone: true,
})
export class TotalDistancePipe implements PipeTransform {
  transform(vertices: LatLng[]): number {
    return vertices.reduce((acc, cur, index, list) => {
      if (index === 0) {
        return 0;
      }
      return (
        acc +
        calculateDistance({
          from: list[index - 1],
          to: cur,
        })
      );
    }, 0);
  }
}

@Pipe({
  name: 'totalArea',
  standalone: true,
})
export class TotalAreaPipe implements PipeTransform {
  transform(vertices: LatLng[]): number {
    const isPolygon = vertices.length >= 3;
    if (!isPolygon) {
      return 0;
    }

    const actualVertices = vertices[0].equals(vertices[vertices.length - 1])
      ? vertices
      : [...vertices, vertices[0]];

    const coordinates = actualVertices.map((vertex) => [
      vertex.lng,
      vertex.lat,
    ]);
    const poly = polygon([coordinates]);
    return area(poly);
  }
}

@Pipe({ name: 'area', standalone: true })
export class AreaPipe implements PipeTransform {
  transform(area: number): string {
    const displayArea =
      area >= 1000000 ? area / 1000000 : area >= 10000 ? area / 10000 : area;

    const unit =
      area >= 1000000
        ? 'km<sup>2</sup>'
        : area >= 10000
          ? 'ha'
          : 'm<sup>2</sup>';

    const numDigits = area >= 10000 && area < 1000000 ? 2 : 1;

    return `${displayArea.toFixed(numDigits)} ${[unit]}`;
  }
}
